import { type ReportQueryParameterType } from '@willow/types-iso';

import {
  firstCodeContentMatches,
  FoundMarker,
  FoundSection,
  hasSelfContainedParens,
  queryMarkerNamesUsed,
  scanOptionalSections,
  scanQueryMarkers,
} from '../queryParameters/queryMarkers';

/**
 * The parameters a report run can supply values for, each with the type that decides how its
 * value renders into SQL and which control the run form shows.
 */
export const REPORT_QUERY_PARAMETERS = {
  report_from_date: 'date',
  report_to_date: 'date',
  report_as_of_date: 'date',
  loan_ids: 'string',
  pool_ids: 'string',
  owner_ids: 'string',
  full_ids: 'string',
  capital_partner_ids: 'string',
  previous_servicer_ids: 'string',
  originator_ids: 'string',
  other_ids: 'string',
  loan_statuses: 'string',
} as const satisfies Record<string, ReportQueryParameterType>;

export type ReportQueryParameterName = keyof typeof REPORT_QUERY_PARAMETERS;

export const REPORT_QUERY_PARAMETER_NAMES = Object.keys(REPORT_QUERY_PARAMETERS) as ReportQueryParameterName[];

export const isReportQueryParameterName = (name: string): name is ReportQueryParameterName =>
  Object.prototype.hasOwnProperty.call(REPORT_QUERY_PARAMETERS, name);

export const reportQueryParameterType = (name: ReportQueryParameterName): ReportQueryParameterType =>
  REPORT_QUERY_PARAMETERS[name];

/** The string-typed parameters — the id filters and the loan-status filter. */
export const isStringReportQueryParameterName = (name: string): name is ReportQueryParameterName =>
  isReportQueryParameterName(name) && REPORT_QUERY_PARAMETERS[name] === 'string';

/** The id filters — the string parameters that match a loan by an identifier, not by status. */
export const isIdFilterParameterName = (name: string): name is ReportQueryParameterName =>
  isStringReportQueryParameterName(name) && name !== 'loan_statuses';

/** Deduped report parameters the SQL uses, in the order this registry declares them. */
export const reportQueryParametersUsed = (sql: string): ReportQueryParameterName[] => {
  const used = new Set(queryMarkerNamesUsed(sql));
  return REPORT_QUERY_PARAMETER_NAMES.filter((name) => used.has(name));
};

/**
 * Which of a run window's two dates the given parameters take. The period start feeds
 * `{{report_from_date}}`; the period end feeds both `{{report_to_date}}` and
 * `{{report_as_of_date}}`, because an as-of query is "as of" the moment its period closed.
 */
export const reportWindowNeedsForParameters = (
  parameterNames: readonly string[],
): { startDate: boolean; endDate: boolean } => ({
  startDate: parameterNames.includes('report_from_date'),
  endDate: parameterNames.includes('report_to_date') || parameterNames.includes('report_as_of_date'),
});

export const reportQueryWindowNeeds = (sql: string): { startDate: boolean; endDate: boolean } =>
  reportWindowNeedsForParameters(reportQueryParametersUsed(sql));

/**
 * Whether a run may drop the reporting period entirely ("all time"). Only true when the query uses
 * at least one date parameter and every date parameter it uses sits inside an optional `[[ ]]`
 * section — the binder strips a section whose parameter has no value, so an all-time run omits the
 * dates and those clauses disappear. A date parameter outside a section cannot be dropped (the
 * binder rejects a run that leaves it empty), so such a query is not all-time eligible.
 */
export const reportQueryAllTimeEligible = (sql: string): boolean => {
  const dateParams = reportQueryParametersUsed(sql).filter((name) => reportQueryParameterType(name) === 'date');
  if (dateParams.length === 0) return false;

  const optional = new Set(scanOptionalSections(sql).sections.flatMap((section) => section.markerNames));
  return dateParams.every((name) => optional.has(name));
};

function getMarkersOutsideSectionIssues(sections: FoundSection[], sql: string) {
  const results: string[] = [];

  const sectionByMarkerStart = new Map<number, number>();
  sections.forEach((section, index) =>
    section.markers.forEach((marker: FoundMarker) => sectionByMarkerStart.set(marker.start, index)),
  );
  for (const marker of scanQueryMarkers(sql).markers) {
    if (isStringReportQueryParameterName(marker.name) && !sectionByMarkerStart.has(marker.start)) {
      results.push(`{{${marker.name}}} is a filter parameter, so it must be used inside an optional [[ ]] section.`);
    }
  }

  return results;
}

function getSectionMultipleFilterParamsIssues(sections: FoundSection[]) {
  const results: string[] = [];

  for (const section of sections) {
    const filterParams = section.markerNames.filter(isStringReportQueryParameterName);
    if (filterParams.length > 1) {
      const names = filterParams.map((name) => `{{${name}}}`).join(', ');
      results.push(
        `An optional [[ ]] section uses ${names}, but a report run supplies only one filter at a time, so a section with more than one filter parameter never applies. Put each filter in its own [[ ]] section.`,
      );
    }
  }

  return results;
}

function getSectionMustStartWithAndIssues(sections: FoundSection[], sql: string) {
  const results: string[] = [];

  for (const section of sections) {
    const content = sql.slice(section.contentStart, section.contentEnd);
    if (!firstCodeContentMatches(content, /^and\b/i)) {
      results.push('An optional [[ ]] section must begin with AND so it can be dropped when its parameter is empty.');
    }
  }

  return results;
}

function getSectionParensIssues(sections: FoundSection[], sql: string) {
  const results: string[] = [];

  for (const section of sections) {
    const content = sql.slice(section.contentStart, section.contentEnd);
    if (!hasSelfContainedParens(content)) {
      results.push('An optional [[ ]] section must open and close its own parentheses.');
    }
  }

  return results;
}

/**
 * Author-facing messages for the problems with a query's optional `[[ ]]` sections.
 */
export const reportQuerySectionIssues = (sql: string): string[] => {
  const issues: string[] = [];
  const { sections, problems } = scanOptionalSections(sql);

  for (const problem of problems) {
    switch (problem.kind) {
      case 'nested':
        issues.push('An optional [[ ]] section cannot contain another [[ ]] section.');
        break;
      case 'unclosed':
        issues.push('An optional [[ section is missing its closing ]].');
        break;
      case 'unmatched-close':
        issues.push('Found ]] with no matching [[ that opens an optional section.');
        break;
      case 'no-param':
        issues.push('An optional [[ ]] section must use at least one parameter, for example {{loan_ids}}.');
        break;
      default:
        break;
    }
  }

  issues.push(...getMarkersOutsideSectionIssues(sections, sql));

  issues.push(...getSectionMultipleFilterParamsIssues(sections));

  issues.push(...getSectionMustStartWithAndIssues(sections, sql));

  issues.push(...getSectionParensIssues(sections, sql));

  return [...new Set(issues)];
};
